import { AddAudienceButton } from '@/features/audienceEdit/components';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Shared audience page header used across features (edit, details, etc.), so
 * title/subtitle must accept runtime i18n keys from any namespace
 * (e.g. `audience_edit:title`, `audiences:subtitle`).
 *
 * `TranslationKeys` uses dotted paths (`audience_edit.title`), which do not
 * match what `t()` / `Trans` resolve here without `toRuntimeTranslationKey`.
 * `TranslationKeysByPath<N>` would lock this component to one namespace.
 *
 * Using `string` keeps the API simple for cross-feature reuse with colon keys.
 */
type AudiencePageHeaderProps = {
  title: string;
  subtitle: string;
  hasButton?: boolean;
};

export const AudiencePageHeader: FC<AudiencePageHeaderProps> = ({ title, subtitle, hasButton = false }) => {
  const { t } = useTranslation();

  return (
    <section className='flex flex-col gap-1 border-b border-secondary-border pb-4'>
      <div className='flex flex-row items-center justify-between'>
        <div className='flex flex-col gap-1'>
          <h1 className='text-2xl font-semibold leading-8 text-primary-900'>{t(title)}</h1>
          <h3 className='text-tertiary-600'>
            <Trans
              i18nKey={subtitle}
              components={{
                newLine: <br />,
              }}
            />
          </h3>
        </div>
        {hasButton && <AddAudienceButton />}
      </div>
    </section>
  );
};
